import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiClient, ApiError } from '../api/client';
import AuthLayout from '../components/AuthLayout';

interface PasswordRequirements {
  min_length: number;
  max_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_number: boolean;
  require_special: boolean;
  special_chars: string;
}

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requirements, setRequirements] = useState<PasswordRequirements | null>(null);
  const [showRequirements, setShowRequirements] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Fetch password requirements on mount
  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        const reqs = await apiClient.getPasswordRequirements();
        setRequirements(reqs);
      } catch (err) {
        console.error('Failed to fetch password requirements:', err);
        // Set defaults if fetch fails
        setRequirements({
          min_length: 12,
          max_length: 128,
          require_uppercase: true,
          require_lowercase: true,
          require_number: true,
          require_special: true,
          special_chars: '!@#$%^&*(),.?":{}|<>',
        });
      }
    };
    fetchRequirements();
  }, []);

  const validatePassword = (password: string): string | null => {
    if (!requirements) {
      return null; // Wait for requirements to load
    }

    if (password.length < requirements.min_length) {
      return `Password must be at least ${requirements.min_length} characters long`;
    }

    if (password.length > requirements.max_length) {
      return `Password must not exceed ${requirements.max_length} characters`;
    }

    if (requirements.require_uppercase && !/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }

    if (requirements.require_lowercase && !/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }

    if (requirements.require_number && !/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }

    if (requirements.require_special) {
      const specialCharsRegex = new RegExp(`[${requirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`);
      if (!specialCharsRegex.test(password)) {
        return `Password must contain at least one special character (${requirements.special_chars})`;
      }
    }

    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    // Validate password
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.resetPassword(token, newPassword);
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // If there are specific requirement errors, show them
        if (err.details?.requirements && Array.isArray(err.details.requirements)) {
          setError(err.details.requirements.join('. '));
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <div>
          <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Invalid Reset Link</h2>
              <p className="mt-2 text-sm text-gray-600">
                This password reset link is invalid or has expired.
              </p>
            <div className="mt-6">
              <Link to="/forgot-password" className="btn-primary block text-center">
                Request New Reset Link
              </Link>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-red-400 mr-2 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* New Password Field */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="newPassword" className="label mb-0">
                  New password
                </label>
                {requirements && (
                  <button
                    type="button"
                    onClick={() => setShowRequirements((v) => !v)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                  >
                    Requirements
                    <svg
                      className={`h-3 w-3 transition-transform ${showRequirements ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}
              </div>
              {showRequirements && requirements && (
                <div className="mb-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 space-y-1">
                  <p>At least {requirements.min_length} characters long</p>
                  {requirements.require_uppercase && requirements.require_lowercase && (
                    <p>Uppercase and lowercase letters</p>
                  )}
                  {requirements.require_uppercase && !requirements.require_lowercase && (
                    <p>At least one uppercase letter</p>
                  )}
                  {requirements.require_lowercase && !requirements.require_uppercase && (
                    <p>At least one lowercase letter</p>
                  )}
                  {requirements.require_number && <p>At least one number</p>}
                  {requirements.require_special && (
                    <p>At least one special character: {requirements.special_chars}</p>
                  )}
                </div>
              )}
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                className="input-field"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="input-field"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Resetting password...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>

        {/* Back to Login */}
        <Link to="/login" className="btn-secondary block text-center">
          Back to Login
        </Link>
      </form>
    </AuthLayout>
  );
}

